import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookieBanner } from "@/components/CookieBanner";
import { ScrollToTop } from "@/components/ScrollToTop";
import { RouteFallback } from "@/components/RouteFallback";
import { BootCleanup } from "@/components/BootCleanup";
import { HelpWidget } from "@/components/HelpWidget";
import { usePageTracking } from "@/hooks/usePageTracking";
import { quickUpdateImport } from "@/lib/routePrefetch";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Admin from "./pages/Admin";
import SubmitUpdate from "./pages/SubmitUpdate";
import ShelterPage from "./pages/ShelterPage";
import ClaimToken from "./pages/auth/ClaimToken";

import PanelLayout from "./pages/panel/PanelLayout";
import PanelDashboard from "./pages/panel/Dashboard";
import PanelEditProfile from "./pages/panel/EditProfile";
import PanelEditNeeds from "./pages/panel/EditNeeds";
import PanelStatistics from "./pages/panel/Statistics";
import PanelGraphics from "./pages/panel/Graphics";
import PanelClaimShelter from "./pages/panel/ClaimShelter";
import PanelTeam from "./pages/panel/Team";
import PanelAcceptTeamInvite from "./pages/panel/AcceptTeamInvite";
import PanelAccountSettings from "./pages/panel/AccountSettings";
import PanelHistory from "./pages/panel/History";
import Unsubscribe from "./pages/Unsubscribe";

// Lazy-loaded routes bez własnego pełnoekranowego loadera.
const QuickUpdate = lazy(quickUpdateImport);
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const ForShelters = lazy(() => import("./pages/ForShelters"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const AddShelterChoice = lazy(() => import("./pages/AddShelterChoice"));
const UpdateShelterChoice = lazy(() => import("./pages/UpdateShelterChoice"));
const ClaimAccess = lazy(() => import("./pages/ClaimAccess"));
const About = lazy(() => import("./pages/About"));

const PageTracker = () => {
  usePageTracking();
  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <Suspense fallback={<RouteFallback />}>
          <BootCleanup />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/index" element={<Index />} />
            <Route path="/index.html" element={<Index />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/dodaj-schronisko" element={<AddShelterChoice />} />
            <Route path="/zglos" element={<SubmitUpdate />} />
            <Route path="/zglos/:slug" element={<SubmitUpdate />} />
            <Route path="/aktualizuj-schronisko" element={<UpdateShelterChoice />} />
            <Route path="/aktualizuj" element={<QuickUpdate />} />
            <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
            <Route path="/regulamin" element={<Terms />} />
            <Route path="/dla-schronisk" element={<ForShelters />} />
            <Route path="/o-projekcie" element={<About />} />
            <Route path="/schronisko/:slug" element={<ShelterPage />} />
            <Route path="/logowanie" element={<Login />} />
            <Route path="/rejestracja" element={<Register />} />
            <Route path="/zapomnialem-hasla" element={<ForgotPassword />} />
            <Route path="/reset-hasla" element={<ResetPassword />} />
            <Route path="/przejmij/:token" element={<ClaimToken />} />
            <Route path="/przejmij/:slug/:token" element={<ClaimToken />} />
            <Route path="/uzyskaj-dostep/:shelterId" element={<ClaimAccess />} />
            <Route path="/panel" element={<PanelLayout />}>
              <Route index element={<PanelDashboard />} />
              <Route path="profil" element={<PanelDashboard />} />
              <Route path="profil/:slug" element={<PanelEditProfile />} />
              <Route path="potrzeby" element={<PanelDashboard />} />
              <Route path="potrzeby/:slug" element={<PanelEditNeeds />} />
              <Route path="statystyki" element={<PanelDashboard />} />
              <Route path="statystyki/:slug" element={<PanelStatistics />} />
              <Route path="grafiki" element={<PanelDashboard />} />
              <Route path="grafiki/:slug" element={<PanelGraphics />} />
              <Route path="historia" element={<PanelDashboard />} />
              <Route path="historia/:slug" element={<PanelHistory />} />
              <Route path="przejmij" element={<PanelClaimShelter />} />
              <Route path="zespol" element={<PanelTeam />} />
              <Route path="zespol/dolacz" element={<PanelAcceptTeamInvite />} />
              <Route path="konto" element={<PanelAccountSettings />} />
            </Route>
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
          <CookieBanner />
          <HelpWidget />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
